import { useMemo, useCallback } from "react";
import {
  Table,
  Card,
  Flex,
  Text,
  TableSummaryCell,
  TableSummaryRow,
} from "@/base/components/index";
import { BillingInvoiceProductItemType } from "@/schemas/index";
import { useItemsStyles } from "./items.styles";
import { usePageContextInvoiceView } from "../../context/invoice-view-context";
import { LocalizationText } from "@/app/index";
import { LocalizationMoney } from "@/app/index";
import { SectionPage } from "@/components/section-page";
import { ProductImage } from "@/components/product-image/index";

export const ModuleItems = () => {
  const { styles } = useItemsStyles();
  const { invoice } = usePageContextInvoiceView();

  const columns: any = useMemo(
    () => [
      {
        dataIndex: "ProductName",
        title: <LocalizationText value="invoice.view.items.column.product" />,
        minWidth: 260,
        render: (value: string, record: BillingInvoiceProductItemType) => (
          <Flex align="center" gap={12}>
            <ProductImage
              src={record?.Product?.DefaultImage?.ThumbnailUrl}
              width={64}
              height={64}
            />
            <Flex vertical>
              <Text>{value}</Text>
              <Text type="secondary" size="small">
                {record?.Product?.ShortDescription}
              </Text>
            </Flex>
          </Flex>
        ),
      },
      {
        dataIndex: "Unit",
        title: <LocalizationText value="invoice.view.items.column.unit" />,
        align: "right",
        minWidth: 90,
      },
      {
        dataIndex: "Quantity",
        title: <LocalizationText value="invoice.view.items.column.qty" />,
        align: "right",
        minWidth: 90,
      },
      {
        dataIndex: "PricePerUnit",
        title: <LocalizationText value="invoice.view.items.column.price" />,
        minWidth: 120,
        align: "right",
        render: (value: BillingInvoiceProductItemType["PricePerUnit"]) =>
          value && <LocalizationMoney value={value} />,
      },
      {
        dataIndex: "BaseAmount",
        title: <LocalizationText value="invoice.view.items.column.amount" />,
        minWidth: 120,
        align: "right",
        render: (value: BillingInvoiceProductItemType["BaseAmount"]) =>
          value && <LocalizationMoney value={value} />,
      },
    ],
    [],
  );

  const renderSummary = useCallback(() => {
    const summaryItems = [
      {
        label: "Subtotal",
        value: invoice?.TotalDetailAmount,
      },
      {
        label: "Discount",
        value: invoice?.TotalDiscountAmount,
      },
      {
        label: "Sales Tax",
        value: invoice?.TotalTax,
      },
      {
        label: "Shipping",
        value: invoice?.TotalLineItemDiscountAmount,
      },
      {
        label: "Total",
        value: invoice?.TotalAmount,
      },
    ];

    return summaryItems?.map(({ label, value }) => (
      <TableSummaryRow key={label}>
        <TableSummaryCell index={0} colSpan={2} className={styles.hidden} />

        <TableSummaryCell index={2} colSpan={2} align="right">
          <Text strong>{label}</Text>
        </TableSummaryCell>

        <TableSummaryCell align="right" index={3}>
          <Text>
            <LocalizationMoney value={value || 0} />
          </Text>
        </TableSummaryCell>
      </TableSummaryRow>
    ));
  }, [invoice]);

  const scrollSetting: any = useMemo(
    () => ({
      x:
        columns?.reduce((acc: number, column: any) => {
          return acc + (column.minWidth || 0);
        }, 0) || undefined,
    }),
    [columns],
  );

  return (
    <SectionPage
      classNames={{
        section: styles.section,
        body: styles.body,
      }}
    >
      <Card>
        <Table<BillingInvoiceProductItemType>
          sticky
          rowKey="Id"
          tableLayout="auto"
          scroll={scrollSetting}
          columns={columns}
          dataSource={invoice?.Items}
          pagination={false}
          summary={renderSummary}
        />
      </Card>
    </SectionPage>
  );
};
